import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import productData from '../../data/products.json';

export default function CategoryProductsPage() {
    const { categoryId } = useParams();
    const categoryProducts = productData.products.filter(
        product => product.categoryId === categoryId
    );
    // Get category info from categories.json
    const categoriesList = require('../../data/categories.json').categories;
    const categoryInfo = categoriesList.find(c => c.id === categoryId);
    const siteUrl = 'https://baggainternational.com';
    const canonicalUrl = `${siteUrl}/categories/${categoryId}`;
    const metaTitle = categoryInfo ? `${categoryInfo.name} Products | Bagga International` : 'Category Products | Bagga International';
    const metaDescription = categoryInfo ? categoryInfo.description : 'Browse products by category at Bagga International.';

    // Default OG/Twitter image fallback
    const defaultImage = `${siteUrl}/images/site-default-og.jpg`;
    const ogImage = (categoryInfo && categoryInfo.image) ? `${siteUrl}${categoryInfo.image}` : defaultImage;
    const meta = {
        title: metaTitle,
        meta: [
            { name: 'description', content: metaDescription },
            { property: 'og:title', content: metaTitle },
            { property: 'og:description', content: metaDescription },
            { property: 'og:url', content: canonicalUrl },
            { property: 'og:type', content: 'website' },
            { property: 'og:image', content: ogImage },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: metaTitle },
            { name: 'twitter:description', content: metaDescription },
            { name: 'twitter:image', content: ogImage },
        ],
        link: [
            { rel: 'canonical', href: canonicalUrl }
        ],
        script: [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    "@context": "https://schema.org/",
                    "@type": "CollectionPage",
                    "name": metaTitle,
                    "description": metaDescription,
                    "url": canonicalUrl,
                    "mainEntity": categoryProducts.map(product => ({
                        "@type": "Product",
                        "name": product.name,
                        "image": product.image,
                        "sku": product.sku
                    }))
                })
            }
        ]
    };

    return (
        <HelmetProvider>
            <Helmet {...meta} />
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{categoryInfo ? categoryInfo.name : 'Category'}</h1>
                <p className="text-lg text-coolGray-600 mb-6">{categoryInfo ? categoryInfo.description : ''}</p>
                {categoryProducts.length === 0 ? (
                    <div className="text-center text-coolGray-500 py-16">No products found in this category.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {categoryProducts.map((product) => (
                            <Link
                                to={`/categories/${product.categoryId}/${product.id}`}
                                key={product.id}
                                className="block hover:no-underline"
                            >
                                <div className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col">
                                    <div className="bg-gray-100 p-4">
                                        <img
                                            src={product.image || '/images/placeholder.png'}
                                            alt={product.name}
                                            className="w-full h-auto rounded-md"
                                        />
                                    </div>
                                    <div className="p-4 flex-1 flex flex-col">
                                        <h5 className="mb-2 text-base leading-tight text-darkCoolGray-900 font-bold tracking-tighter">{product.name}</h5>
                                        <p className="text-coolGray-500 mb-3">{product.sku}</p>
                                        <span className="mt-auto inline-block w-full py-2 px-4 bg-green-500 text-white text-center rounded-md font-medium hover:bg-green-600 transition">
                                            See Details
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </HelmetProvider>
    );
}
